import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import {
  Block,
  BlockBetween,
  BlockDes,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Button,
  Col,
  Row,
  Icon,
  RSelect,
} from "../../../components/Component";
import { useHistory, useParams } from "react-router-dom";
import http from "../../../utils/http-common";
import { getPublicUrl } from "../../../utils/Utils";
import { formatAdminApiError } from "../../../utils/formatAdminApiError";
import {
  flattenCategories,
  filterCategoriesBySearch,
  computeVisibleCategories,
  getClinicPriceCurrencySymbol,
  resolveClinicPriceCurrencySelectProps,
  tagCatalogRowsWithChannel,
} from "../../../utils/clinicPriceGrid";
import swal from "sweetalert";
import { Card, FormGroup, Alert } from "reactstrap";

/** When false, edits stay local until the user clicks Save (avoids surprise API calls during incidents). */
const ENABLE_PRICE_AUTOSAVE = false;

const devLog = (...args) => {
  if (process.env.NODE_ENV === "development") {
    // eslint-disable-next-line no-console
    console.log(...args);
  }
};
const devWarn = (...args) => {
  if (process.env.NODE_ENV === "development") {
    // eslint-disable-next-line no-console
    console.warn(...args);
  }
};
const devError = (...args) => {
  if (process.env.NODE_ENV === "development") {
    // eslint-disable-next-line no-console
    console.error(...args);
  }
};

const ClinicPrices = () => {
  const { id: clinicId } = useParams();
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [clinic, setClinic] = useState(null);
  const [partner, setPartner] = useState(null);
  /** Medical tourism / treatment tree (platform root: setting or uid 48). */
  const [categoriesTm, setCategoriesTm] = useState([]);
  /** Teleconsultation specialties tree (platform root: setting or uid 192). */
  const [categoriesTc, setCategoriesTc] = useState([]);
  const [pricesMap, setPricesMap] = useState({});
  // eslint-disable-next-line no-unused-vars
  const [initialPricesMap, setInitialPricesMap] = useState({}); // For comparison (used via setInitialPricesMap)
  const [inputValues, setInputValues] = useState({}); // Temporary string values for inputs (allows typing)
  /** Clinic default currency (TM + TC); persisted on clinic as defaultCurrency. */
  const [globalCurrency, setGlobalCurrency] = useState("USD");
  const [savingClinicCurrency, setSavingClinicCurrency] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [channelFilter, setChannelFilter] = useState("all");
  const [expandedHeaders, setExpandedHeaders] = useState(new Set());
  const [autoSaving, setAutoSaving] = useState(false);
  const [saving, setSaving] = useState(false); // For manual save
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [tcClinicForm, setTcClinicForm] = useState({
    price: "",
    duration: 20,
    bookingMode: "named_practitioner",
  });
  const [savingTcClinic, setSavingTcClinic] = useState(false);

  const gridCurrencySelectProps = useMemo(
    () => resolveClinicPriceCurrencySelectProps(globalCurrency, { emptyDefault: "USD" }),
    [globalCurrency]
  );

  const partnerBackId = partner?._id || clinic?.partner?._id || clinic?.partner;
  const navigateBackFromClinicPrices = useCallback(() => {
    const pid = partner?._id || clinic?.partner?._id || clinic?.partner;
    if (pid) {
      history.push(getPublicUrl(`medical-partner-details/${String(pid)}`));
      return;
    }
    history.push(getPublicUrl("clinics"));
  }, [history, partner, clinic]);

  // Refs for memory leak prevention and race condition handling
  const saveTimeoutRef = useRef(null);
  const isMountedRef = useRef(true);
  const isSavingRef = useRef(false);
  const abortControllerRef = useRef(null);
  const pendingNavigationRef = useRef(null);

  // Safe setState wrapper to prevent memory leaks
  const safeSetState = useCallback((setter, value) => {
    if (isMountedRef.current) {
      setter(value);
    }
  }, []);

  /** Suggested default currency from clinic address country (hint only; admin chooses). */
  const suggestedCurrencyFromCountry = useMemo(() => {
    const raw = clinic?.address?.country;
    if (!raw || typeof raw !== "string") return null;
    const t = raw.trim().toLowerCase();
    const map = {
      france: "EUR",
      germany: "EUR",
      spain: "EUR",
      italy: "EUR",
      netherlands: "EUR",
      belgium: "EUR",
      portugal: "EUR",
      ireland: "EUR",
      "united kingdom": "GBP",
      uk: "GBP",
      england: "GBP",
      scotland: "GBP",
      wales: "GBP",
      "united states": "USD",
      usa: "USD",
      canada: "CAD",
      turkey: "TRY",
      türkiye: "TRY",
      morocco: "MAD",
      tunisia: "TND",
      switzerland: "CHF",
      india: "INR",
    };
    return map[t] || null;
  }, [clinic?.address?.country]);

  // Load clinic data
  useEffect(() => {
    const fetchClinicData = async () => {
      try {
        setLoading(true);
        const clinicResponse = await http.get(`/api/v1/partners/medical/clinics/${clinicId}`, {
          suppressInlineErrorSwal: true,
        });
        const clinicData = clinicResponse.data?.data || clinicResponse.data;
        setClinic(clinicData);
        setTcClinicForm({
          price:
            clinicData.consultationPrice != null && clinicData.consultationPrice !== ""
              ? String(clinicData.consultationPrice)
              : "",
          duration:
            clinicData.consultationDurationMinutes != null
              ? String(clinicData.consultationDurationMinutes)
              : "20",
          bookingMode: clinicData.teleconsultationBookingMode || "named_practitioner",
        });

        // Load partner
        let partnerIdForPrices = null;
        if (clinicData.partner) {
          const partnerId =
            clinicData.partner._id || clinicData.partner || clinicData.partnerId;
          partnerIdForPrices = partnerId;
          try {
            const partnerResponse = await http.get(`/api/v1/medical-partners/${partnerId}`, {
              suppressInlineErrorSwal: true,
            });
            const partnerData =
              partnerResponse.data?.data || partnerResponse.data;
            setPartner(partnerData);
          } catch (error) {
            // Error loading partner - non-blocking
            if (process.env.NODE_ENV === 'development') {
              devError("Error loading partner:", error);
            }
          }
        }

        // Load prices
        if (partnerIdForPrices) {
          try {
            const pricesResponse = await http.get(
              `/api/v1/partners/medical/prices/${partnerIdForPrices}?clinicId=${clinicId}`,
              { suppressInlineErrorSwal: true }
            );
            const pricesData = pricesResponse.data?.data?.prices || [];

            // Build prices map - ensure all categoryIds are strings for consistency
            const map = {};
            pricesData.forEach((price) => {
              const categoryId =
                price.categoryId?._id || price.categoryId || price.category?._id;
              if (categoryId) {
                // Convert to string to ensure consistent key format
                const catId = String(categoryId);
                map[catId] = {
                  price: price.price || 0,
                  currency: price.currency || "USD",
                  isActive: price.isActive !== undefined ? price.isActive : true,
                  description: price.description || "",
                  duration: price.duration || 0,
                  isTeleconsultation: price.isTeleconsultation === true,
                };
              }
            });
            setPricesMap(map);
            setInitialPricesMap(JSON.parse(JSON.stringify(map))); // Deep copy for comparison
            setGlobalCurrency(
              clinicData.defaultCurrency || pricesData[0]?.currency || "USD"
            );
            setHasUnsavedChanges(false); // Reset after loading
            
            // Synchronize inputValues with loaded pricesMap
            // Ensure all categoryIds are strings for consistency
            const syncedInputValues = {};
            Object.entries(map).forEach(([categoryId, data]) => {
              const catId = String(categoryId); // Ensure string format
              if (data.price !== undefined && data.price !== null) {
                syncedInputValues[`${catId}-price`] = String(data.price);
              }
              if (data.duration !== undefined && data.duration !== null) {
                syncedInputValues[`${catId}-duration`] = String(data.duration);
              }
            });
            setInputValues(syncedInputValues);
          } catch (error) {
            // Prices might not exist yet, that's okay
            if (process.env.NODE_ENV === 'development') {
              devError("Error loading prices:", error);
            }
            setPricesMap({});
          }
        }

      } catch (error) {
        if (process.env.NODE_ENV === "development") {
          devError("Error loading clinic data:", error);
        }
        swal("Could not load clinic prices", formatAdminApiError(error, "Failed to load data"), "error");
      } finally {
        setLoading(false);
      }
    };

    if (clinicId) {
      fetchClinicData();
    }
  }, [clinicId]);

  // Load settings (catalog roots) + category tree for price grid
  useEffect(() => {
    let cancelled = false;
    const loadCategories = async () => {
      try {
        const [settingsRes, response] = await Promise.all([
          http.get("/api/v1/settings").catch(() => ({ data: { data: [] } })),
          http.post("/api/v1/category/list", {}),
        ]);
        if (cancelled) return;
        const rows = settingsRes.data?.data || [];
        const byKey = {};
        rows.forEach((r) => {
          if (r?.key) byKey[r.key] = r.value != null ? String(r.value).trim() : "";
        });

        const flat = response.data?.data;
        if (!Array.isArray(flat)) return;

        const mtFromSetting = byKey.medical_tourism_catalog_root_category_id;
        const mtFallback = flat.find((cat) => cat.uid === 48);
        const medicalRootId = mtFromSetting || mtFallback?._id;

        const tcFromSetting = byKey.tc_specialties_root_category_id;
        const tcFallback = flat.find((cat) => cat.uid === 192);
        const tcRootId = tcFromSetting || tcFallback?._id;

        const buildTree = (parentId) => {
          return flat
            .filter((cat) => {
              const parent = cat.parent?._id || cat.parent;
              return parent != null && String(parent) === String(parentId);
            })
            .map((cat) => ({
              _id: cat._id,
              uid: cat.uid,
              label: cat.label,
              parent: cat.parent,
              children: buildTree(cat._id),
            }))
            .sort((a, b) => (a.label || "").localeCompare(b.label || ""));
        };

        if (medicalRootId) {
          const medicalCategories = buildTree(medicalRootId);
          setCategoriesTm(flattenCategories(medicalCategories));
        } else {
          setCategoriesTm([]);
        }

        if (tcRootId) {
          const tcCategories = buildTree(tcRootId);
          setCategoriesTc(flattenCategories(tcCategories));
        } else {
          setCategoriesTc([]);
        }
      } catch (error) {
        if (process.env.NODE_ENV === "development") {
          devError("Error loading categories:", error);
        }
      }
    };
    loadCategories();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      // Clear timeout
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = null;
      }
      // Abort ongoing request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, []);

  const tmCategoryIdSet = useMemo(
    () => new Set((categoriesTm || []).map((c) => String(c.id))),
    [categoriesTm]
  );
  const tcCategoryIdSet = useMemo(
    () => new Set((categoriesTc || []).map((c) => String(c.id))),
    [categoriesTc]
  );

  const mergedCategoriesFlat = useMemo(
    () => [
      ...tagCatalogRowsWithChannel(categoriesTm, "tm"),
      ...tagCatalogRowsWithChannel(categoriesTc, "tc"),
    ],
    [categoriesTm, categoriesTc]
  );

  const categoriesForChannelFilter = useMemo(() => {
    if (channelFilter === "all") return mergedCategoriesFlat;
    return mergedCategoriesFlat.filter((c) => c.catalogChannel === channelFilter);
  }, [mergedCategoriesFlat, channelFilter]);

  const filteredCategories = useMemo(
    () => filterCategoriesBySearch(categoriesForChannelFilter, searchText),
    [categoriesForChannelFilter, searchText]
  );

  const visibleCategories = useMemo(
    () => computeVisibleCategories(filteredCategories, expandedHeaders),
    [filteredCategories, expandedHeaders]
  );

  const defaultCurrencyForCategory = useCallback(
    (_categoryId) => clinic?.defaultCurrency || globalCurrency || "USD",
    [clinic?.defaultCurrency, globalCurrency]
  );

  /** Persisted on each price row; drives patient TC vs TM resolution per ADR. */
  const resolveTeleconsultationFlag = useCallback(
    (categoryId, explicit) => {
      if (typeof explicit === "boolean") return explicit;
      const id = String(categoryId);
      if (tcCategoryIdSet.has(id)) return true;
      if (tmCategoryIdSet.has(id)) return false;
      const products = clinic?.clinicProducts || [];
      const row = products.find((p) => String(p.category?._id || p.category) === id);
      if (row) return row.isTeleconsultation === true;
      return false;
    },
    [clinic?.clinicProducts, tcCategoryIdSet, tmCategoryIdSet]
  );

  const toggleHeader = useCallback((categoryId) => {
    const id = String(categoryId);
    setExpandedHeaders((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const saveClinicDefaultCurrency = useCallback(async () => {
    if (!clinicId) return;
    setSavingClinicCurrency(true);
    try {
      await http.put(
        `/api/v1/partners/medical/clinics/${clinicId}`,
        { defaultCurrency: globalCurrency },
        { suppressInlineErrorSwal: true }
      );
      const refreshed = await http.get(`/api/v1/partners/medical/clinics/${clinicId}`, {
        suppressInlineErrorSwal: true,
      });
      const c = refreshed.data?.data || refreshed.data;
      setClinic(c);
      swal("Saved", "Clinic default currency updated.", "success");
    } catch (err) {
      swal("Could not save currency", formatAdminApiError(err, "Save failed"), "error");
    } finally {
      setSavingClinicCurrency(false);
    }
  }, [clinicId, globalCurrency]);

  // Internal save function (used by both auto-save and manual save)
  const performSave = useCallback(async (updatedPricesMap, isManual = false) => {
    // For manual save, we've already handled aborting previous requests in handleManualSave
    // So we can proceed directly. For auto-save, we need to check if a save is in progress.
    if (!isManual && isSavingRef.current) {
      if (process.env.NODE_ENV === 'development') {
        devLog('Save already in progress, skipping...');
      }
      return;
    }

    try {
      isSavingRef.current = true;
      
      // Cancel any ongoing request (only if not already cleared by handleManualSave)
      if (abortControllerRef.current) {
        const oldController = abortControllerRef.current;
        abortControllerRef.current = null; // Clear ref before aborting
        oldController.abort();
        // Wait a bit for abort to complete
        await new Promise(resolve => setTimeout(resolve, 200));
      }
      
      // Create new AbortController
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      if (isManual) {
        safeSetState(setSaving, true);
      } else {
        safeSetState(setAutoSaving, true);
      }

      // Enhanced validation before sending
        const pricesData = Object.entries(updatedPricesMap || pricesMap)
          .filter(([categoryId, data]) => {
          // Validate categoryId format (MongoDB ObjectId is 24 hex characters)
          if (!categoryId || typeof categoryId !== 'string' || categoryId.length !== 24) {
            if (process.env.NODE_ENV === 'development') {
              devWarn(`Invalid categoryId format: ${categoryId}`);
            }
            return false;
          }
          if (!data || typeof data !== 'object') {
            return false;
          }
          
          // Validate price
            const price = parseFloat(data.price);
          if (isNaN(price) || price < 0) {
            return false;
          }
          
          // Validate: active price must be > 0 (but allow inactive prices with 0)
          // Note: We allow price 0 even if active, as the backend will handle it
          // The backend validation is the source of truth
          
          // Validate duration (must be non-negative integer)
          const duration = parseInt(data.duration);
          if (isNaN(duration) || duration < 0) {
            return false;
          }
          
            return true;
          })
        .map(([categoryId, data]) => {
          const price = parseFloat(data.price);
          const duration = parseInt(data.duration) || 0;
          
          return {
            categoryId,
            price: Math.round(price * 100) / 100, // Round to 2 decimal places
            currency: data.currency || defaultCurrencyForCategory(categoryId) || "USD",
            isActive: data.isActive === true,
            description: (data.description || "").trim().substring(0, 500), // Limit description length
            duration: Math.max(0, duration), // Ensure non-negative
            isTeleconsultation: resolveTeleconsultationFlag(
              categoryId,
              typeof data.isTeleconsultation === "boolean" ? data.isTeleconsultation : undefined
            ),
          };
        });

      // Validation: check if we have at least one valid price to save
      // But allow saving even if all prices are 0, as user might want to clear prices
        if (pricesData.length === 0) {
        if (isManual) {
          safeSetState(setSaving, false);
          swal({
            title: "No data to save",
            text: "No valid price found. Please fill at least one price before saving.",
            icon: "info",
            button: "OK"
          });
        } else {
          safeSetState(setAutoSaving, false);
        }
        isSavingRef.current = false;
        abortControllerRef.current = null;
          return;
        }

      // Get partnerId before using it
        const partnerId =
          partner?._id || clinic?.partner?._id || clinic?.partner;
      
      // Convert to string if it's an ObjectId
      let partnerIdString = partnerId?.toString ? partnerId.toString() : partnerId;
      
      // Validate partnerId format (MongoDB ObjectId is 24 hex characters)
      if (!partnerIdString || typeof partnerIdString !== 'string' || partnerIdString.length !== 24) {
        devError('Invalid partnerId:', {
          partnerId,
          partnerIdString,
          partner: partner?._id,
          clinicPartner: clinic?.partner,
          clinicPartnerId: clinic?.partner?._id
        });
        if (isManual) {
          safeSetState(setSaving, false);
          swal({
            title: "Error",
            text: "Could not determine the partner. Please reload the page.",
            icon: "error",
            button: "OK"
          });
        } else {
          safeSetState(setAutoSaving, false);
        }
        isSavingRef.current = false;
        abortControllerRef.current = null;
        return;
      }
      
      // Log what we're sending for debugging
      const targetPriceData = pricesData.find(p => p.categoryId === '669bb844f6943422f19df70e');
      devLog('Saving prices:', {
        count: pricesData.length,
        partnerId: partnerIdString,
        partnerIdRaw: partnerId,
        partnerIdType: typeof partnerId,
        clinicId,
        samplePrice: pricesData[0],
        allPrices: pricesData.map(p => ({ 
          categoryId: p.categoryId, 
          price: p.price, 
          isActive: p.isActive,
          duration: p.duration,
          currency: p.currency
        })),
        targetCategoryId: '669bb844f6943422f19df70e',
        targetPrice: targetPriceData?.price,
        targetPriceData: targetPriceData,
        updatedPricesMapSample: updatedPricesMap ? Object.keys(updatedPricesMap).slice(0, 2).map(k => ({
          catId: k,
          price: updatedPricesMap[k]?.price,
          duration: updatedPricesMap[k]?.duration
        })) : null
      });

        if (!partnerIdString) {
        if (isManual) {
          safeSetState(setSaving, false);
        } else {
          safeSetState(setAutoSaving, false);
        }
        isSavingRef.current = false;
        abortControllerRef.current = null;
          return;
        }

      // Perform save with AbortController signal
      const token = localStorage.getItem("accessToken");
      devLog('About to send PUT request:', {
        url: `/api/v1/partners/medical/prices/${partnerIdString}?clinicId=${clinicId}`,
        pricesCount: pricesData.length,
        firstPrice: pricesData[0],
        hasToken: !!token,
        tokenLength: token ? token.length : 0
      });
      
      // Check if already aborted before sending
      if (abortController.signal.aborted) {
        if (process.env.NODE_ENV === 'development') {
          devLog('Request aborted before sending');
        }
        return;
      }
      
      const fullUrl = `/api/v1/partners/medical/prices/${partnerIdString}?clinicId=${clinicId}`;
      devLog('Full request URL:', fullUrl);
      devLog('PartnerId string:', partnerIdString, 'Type:', typeof partnerIdString, 'Length:', partnerIdString?.length);
      devLog('ClinicId:', clinicId, 'Type:', typeof clinicId);
      
      const response = await http.put(
        fullUrl,
        { prices: pricesData },
        { signal: abortController.signal, suppressInlineErrorSwal: true }
      );
      
      devLog('PUT request completed:', {
        status: response.status,
        success: response.data?.success,
        data: response.data,
        isAborted: abortController.signal.aborted
      });

      // Check if request was aborted
      if (abortController.signal.aborted) {
        return;
      }

      // Verify that the save was actually successful by checking response
      if (!response.data || !response.data.success) {
        throw new Error('Backend did not confirm successful save');
      }

      // Update initialPricesMap immediately with what we sent (before reload)
      // This ensures hasUnsavedChanges is correctly reset
      const currentPricesMap = updatedPricesMap || pricesMap;
      setInitialPricesMap(JSON.parse(JSON.stringify(currentPricesMap)));
      
      // Reset hasUnsavedChanges immediately after successful save
      setHasUnsavedChanges(false);

      // Reload prices from backend to ensure we have the latest data (async, non-blocking)
      // This is done after resetting hasUnsavedChanges to avoid blocking the UI
      try {
        const pricesResponse = await http.get(
          `/api/v1/partners/medical/prices/${partnerIdString}?clinicId=${clinicId}`,
          { signal: abortController.signal, suppressInlineErrorSwal: true }
        );
        
        if (!abortController.signal.aborted && pricesResponse.data?.data?.prices) {
          const reloadedPricesData = pricesResponse.data.data.prices;
          devLog('Reloaded prices data:', {
            count: reloadedPricesData.length,
            firstPrice: reloadedPricesData[0],
            allPrices: reloadedPricesData.map(p => ({
              categoryId: p.categoryId?._id || p.categoryId || p.category?._id,
              price: p.price,
              duration: p.duration,
              isActive: p.isActive
            })),
            targetCategoryId: '669bb844f6943422f19df70e',
            targetPrice: reloadedPricesData.find(p => {
              const catId = p.categoryId?._id || p.categoryId || p.category?._id;
              return String(catId) === '669bb844f6943422f19df70e';
            })?.price
          });
          const reloadedMap = {};
          reloadedPricesData.forEach((price) => {
            // Handle categoryId as object with _id or as direct _id
            let categoryId;
            if (price.categoryId) {
              if (typeof price.categoryId === 'object' && price.categoryId._id) {
                categoryId = price.categoryId._id;
              } else if (typeof price.categoryId === 'string') {
                categoryId = price.categoryId;
              } else {
                categoryId = price.categoryId;
              }
            } else if (price.category) {
              categoryId = price.category._id || price.category;
            }
            
            if (categoryId) {
              const catId = String(categoryId);
              reloadedMap[catId] = {
                price: price.price || 0,
                currency: price.currency || "USD",
                isActive: price.isActive !== undefined ? price.isActive : true,
                description: price.description || "",
                duration: price.duration || 0,
                isTeleconsultation: price.isTeleconsultation === true,
              };
              devLog('Mapped price:', { catId, price: price.price, isActive: price.isActive });
            } else {
              devWarn('No categoryId found for price:', price);
            }
          });
          
          devLog('Final reloadedMap:', reloadedMap);
          
          // Update state with reloaded data
          setPricesMap(reloadedMap);
          setInitialPricesMap(JSON.parse(JSON.stringify(reloadedMap)));
          
          // Synchronize inputValues with reloaded data
          const syncedInputValues = {};
          Object.entries(reloadedMap).forEach(([categoryId, data]) => {
            const catId = String(categoryId);
            if (data.price !== undefined && data.price !== null) {
              syncedInputValues[`${catId}-price`] = String(data.price);
            }
            if (data.duration !== undefined && data.duration !== null) {
              syncedInputValues[`${catId}-duration`] = String(data.duration);
            }
          });
          setInputValues(syncedInputValues);
        }
      } catch (reloadError) {
        // If reload fails, we already updated initialPricesMap above, so hasUnsavedChanges is already false
        if (process.env.NODE_ENV === 'development') {
          devWarn('Failed to reload prices after save (non-critical):', reloadError);
        }
      }

      if (isManual) {
        safeSetState(setSaving, false);
        swal("Saved", "Changes saved successfully.", "success");
      } else {
        safeSetState(setAutoSaving, false);
      }
      } catch (error) {
      // Ignore abort errors
      if (error.name === 'AbortError' || error.name === 'CanceledError' || error.message === 'Cancel' || error.message?.includes('canceled')) {
        if (process.env.NODE_ENV === 'development') {
          devLog('Request aborted (ignoring):', error.name, error.message);
        }
        return;
      }

        if (process.env.NODE_ENV === 'development') {
        devError("Error saving:", error);
        }

      const statusCode = error.response?.status;
      let userMessage = "Could not save changes.";
      if (statusCode === 401) {
        userMessage = "Your session has expired. Please log in again.";
      } else if (statusCode === 403) {
        userMessage = "You don't have permission to edit these prices.";
      } else if (statusCode === 404) {
        userMessage = "Partner or clinic not found.";
      } else if (statusCode >= 500) {
        userMessage = "Server error. Please try again in a few moments.";
      } else if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
        userMessage = "Request timed out. Check your connection and try again.";
      } else if (error.message?.includes("Network Error")) {
        userMessage = "Connection error. Check your internet connection.";
      }
      const detailText = formatAdminApiError(error, userMessage);

      if (isManual) {
        safeSetState(setSaving, false);
        swal({
          title: "Save error",
          text: detailText,
          icon: "error",
          buttons: {
            retry: {
              text: "Retry",
              value: "retry",
              className: "btn-primary"
            },
            cancel: {
              text: "Close",
              value: "cancel",
              className: "btn-secondary"
            }
          }
        }).then((value) => {
          if (value === "retry") {
            handleManualSave();
          }
        });
      } else {
        safeSetState(setAutoSaving, false);
        if (error.name === 'CanceledError' || error.message?.includes('Cancel') || error.code === 'ERR_CANCELED') {
          if (process.env.NODE_ENV === 'development') {
            devLog("Auto-save canceled (expected when manual save is triggered)");
          }
          return;
        }
        swal({
          title: "Auto-save failed",
          text: `${detailText}. Your changes are not lost — click "Save" to retry.`,
          icon: "warning",
          button: "OK",
          timer: 5000,
          timerProgressBar: true,
        });
        if (process.env.NODE_ENV === "development") {
          devError("Auto-save failed:", detailText, error);
        }
      }
      } finally {
      isSavingRef.current = false;
      // Only clear abortControllerRef if it matches the one we created
      if (abortControllerRef.current) {
        abortControllerRef.current = null;
      }
    }
  // handleManualSave omitted intentionally (defined after performSave, circular dep)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pricesMap, partner, clinic, clinicId, safeSetState, resolveTeleconsultationFlag, defaultCurrencyForCategory]);

  // Auto-save with debounce (like Google Sheets)
  const autoSave = useCallback((updatedPricesMap) => {
    if (!ENABLE_PRICE_AUTOSAVE) return;
    // Cancel any pending auto-save
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }

    // Set new timeout
    saveTimeoutRef.current = setTimeout(() => {
      if (isMountedRef.current && !isSavingRef.current) {
        performSave(updatedPricesMap, false);
      }
      saveTimeoutRef.current = null;
    }, 1500);
  }, [performSave]);

  // Manual save function (cancels auto-save and saves immediately)
  const handleManualSave = useCallback(async () => {
    // Cancel pending auto-save
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }

    // Wait for any ongoing save to complete (max 5 seconds)
    let waitCount = 0;
    while (isSavingRef.current && waitCount < 50) {
      await new Promise(resolve => setTimeout(resolve, 100));
      waitCount++;
    }

    // If still saving, abort the current request and wait for it to complete
    if (isSavingRef.current && abortControllerRef.current) {
      const oldAbortController = abortControllerRef.current;
      // Clear the ref BEFORE aborting to prevent performSave from using the old controller
      abortControllerRef.current = null;
      // Reset saving state BEFORE aborting
      isSavingRef.current = false;
      // Now abort the old request
      oldAbortController.abort();
      // Wait for abort to complete (the request should handle the abort gracefully)
      await new Promise(resolve => setTimeout(resolve, 500));
    }

    // Build current pricesMap by combining pricesMap with inputValues
    // This ensures we have the absolute latest values from both sources
    const currentPricesMap = { ...pricesMap };
    
    // Update from inputValues (which contains the latest user input)
    Object.keys(inputValues).forEach((key) => {
      const [catId, field] = key.split('-');
      if (catId && field && (field === 'price' || field === 'duration')) {
        const value = inputValues[key];
        const numValue = value === "" || value === null || value === undefined 
          ? 0 
          : (isNaN(parseFloat(value)) ? 0 : parseFloat(value));
        
        if (!currentPricesMap[catId]) {
          currentPricesMap[catId] = {
            price: 0,
            duration: 0,
            isActive: false,
            currency: defaultCurrencyForCategory(catId),
            description: "",
            isTeleconsultation: resolveTeleconsultationFlag(catId, undefined),
          };
        }
        currentPricesMap[catId][field] = numValue;
      }
    });

    // Save immediately with the most up-to-date values
    await performSave(currentPricesMap, true);
  }, [pricesMap, inputValues, performSave, resolveTeleconsultationFlag, defaultCurrencyForCategory]);

  // Handle price change - allow string input for better UX
  const handlePriceChange = useCallback((categoryId, field, value) => {
    // Ensure categoryId is a string
    const catId = String(categoryId);
    
    // Store raw string value in inputValues for price/duration fields (allows typing)
    if (field === "price" || field === "duration") {
      const inputKey = `${catId}-${field}`;
      
      // Update inputValues immediately to reflect user input
      setInputValues((prev) => ({
        ...prev,
        [inputKey]: value === "" ? "" : String(value),
      }));

      // Convert to number only if value is not empty and valid
      let processedValue;
      if (value === "" || value === null || value === undefined) {
        processedValue = 0;
      } else {
        const numValue = parseFloat(value);
        processedValue = isNaN(numValue) ? 0 : numValue;
    }

      // Update pricesMap with numeric value
    setPricesMap((prev) => {
        const prevData = prev[catId] || {
        price: 0,
          duration: 0,
        isActive: false,
        currency: defaultCurrencyForCategory(catId),
        description: "",
        isTeleconsultation: resolveTeleconsultationFlag(catId, undefined),
      };
      const updated = {
        ...prev,
          [catId]: {
          ...prevData,
          [field]: processedValue,
        },
      };

      // Auto-activate if price changes from 0 to > 0
      if (field === "price" && prevData.price === 0 && processedValue > 0) {
          updated[catId].isActive = true;
      }

        // Mark as having unsaved changes
        setHasUnsavedChanges(true);

      // Trigger auto-save
      autoSave(updated);

      return updated;
    });
    } else if (field === "isActive") {
      const catId = String(categoryId);
      const processedValue = Boolean(value);
      setPricesMap((prev) => {
        const prevData = prev[catId] || {
          price: 0,
          isActive: false,
          currency: defaultCurrencyForCategory(catId),
          description: "",
          isTeleconsultation: resolveTeleconsultationFlag(catId, undefined),
        };
        const updated = {
          ...prev,
          [catId]: {
            ...prevData,
            [field]: processedValue,
          },
        };
        setHasUnsavedChanges(true);
        autoSave(updated);
        return updated;
      });
    } else {
      // For other fields (description, etc.)
      const catId = String(categoryId);
      setPricesMap((prev) => {
        const prevData = prev[catId] || {
          price: 0,
          isActive: false,
          currency: defaultCurrencyForCategory(catId),
          description: "",
          isTeleconsultation: resolveTeleconsultationFlag(catId, undefined),
        };
        const updated = {
          ...prev,
          [catId]: {
            ...prevData,
            [field]: value,
          },
        };
        setHasUnsavedChanges(true);
        autoSave(updated);
        return updated;
      });
    }
  }, [autoSave, resolveTeleconsultationFlag, defaultCurrencyForCategory]);

  // Handle blur - finalize the value (convert empty string to 0)
  const handlePriceBlur = useCallback((categoryId, field) => {
    const catId = String(categoryId);
    setInputValues((prev) => {
      const inputKey = `${catId}-${field}`;
      const inputValue = prev[inputKey];
      
      if (inputValue === "" || inputValue === null || inputValue === undefined) {
        // Clear the input value and set to 0 in pricesMap
        const updated = { ...prev };
        delete updated[inputKey];
        
        setPricesMap((prevPrices) => {
          const prevData = prevPrices[catId] || {
            price: 0,
            duration: 0,
            isActive: false,
            currency: defaultCurrencyForCategory(catId),
            description: "",
            isTeleconsultation: resolveTeleconsultationFlag(catId, undefined),
          };
          return {
            ...prevPrices,
            [catId]: {
              ...prevData,
              [field]: 0,
            },
          };
        });
        
        return updated;
      } else {
        // Ensure the value is properly formatted
        const numValue = parseFloat(inputValue);
        if (!isNaN(numValue)) {
          const updated = { ...prev };
          updated[inputKey] = String(numValue);
          return updated;
        }
        return prev;
      }
    });
  }, [resolveTeleconsultationFlag, defaultCurrencyForCategory]);

  // Intercept React Router navigation with history listener
  useEffect(() => {
    if (!hasUnsavedChanges) return;

    const unblock = history.block((location, action) => {
      if (hasUnsavedChanges && !isSavingRef.current) {
        pendingNavigationRef.current = { location, action };
        swal({
                      title: "Unsaved changes",
                      text: "You have unsaved changes. What would you like to do?",
                      icon: "warning",
                      buttons: {
                        save: {
                          text: "Save and leave",
                          value: "save",
                          className: "btn-primary"
                        },
                        cancel: {
                          text: "Cancel",
                          value: "cancel",
                          className: "btn-secondary"
                        },
                        discard: {
                          text: "Leave without saving",
                          value: "discard",
                          className: "btn-danger"
                        }
                      },
          dangerMode: true
        }).then((value) => {
          if (value === "save") {
            // Save and then navigate
            performSave(pricesMap, true).then(() => {
              setHasUnsavedChanges(false);
              if (pendingNavigationRef.current) {
                const { location: loc } = pendingNavigationRef.current;
                pendingNavigationRef.current = null;
                unblock(); // Unblock navigation
                history.push(loc.pathname);
              }
            });
          } else if (value === "discard") {
            // Discard changes and navigate
            setHasUnsavedChanges(false);
            if (pendingNavigationRef.current) {
              const { location: loc } = pendingNavigationRef.current;
              pendingNavigationRef.current = null;
              unblock(); // Unblock navigation
              history.push(loc.pathname);
            }
          } else {
            // Cancel navigation - keep block active
            pendingNavigationRef.current = null;
          }
        });
        return false; // Block navigation
      }
      return true; // Allow navigation
    });

    return () => {
      unblock(); // Cleanup: unblock on unmount
    };
  }, [hasUnsavedChanges, pricesMap, performSave, history]);

  // Protection beforeunload (browser close/refresh)
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      // Block if saving is in progress
      if (isSavingRef.current || saving || autoSaving) {
        e.preventDefault();
        e.returnValue = 'A save is in progress. Please wait…';
        return 'A save is in progress. Please wait…';
      }
      
      if (hasUnsavedChanges && !isSavingRef.current) {
        e.preventDefault();
        e.returnValue = "You have unsaved changes. Are you sure you want to leave?";
        return e.returnValue;
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [hasUnsavedChanges, saving, autoSaving]);

  if (loading) {
    return (
      <Content>
        <Block>
          <div className="text-center p-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading…</span>
            </div>
          </div>
        </Block>
      </Content>
    );
  }

  if (!clinic) {
    return (
      <Content>
        <Block>
          <div className="text-center p-5">
            <h4>Clinic not found</h4>
            <Button onClick={() => history.goBack()}>Back</Button>
          </div>
        </Block>
      </Content>
    );
  }

  const tcDefaultPriceNum = parseFloat(String(tcClinicForm.price || "").replace(",", "."), 10);
  const showTcPositivePriceWarning =
    clinic?.offersTeleconsultation === true &&
    (!Number.isFinite(tcDefaultPriceNum) || tcDefaultPriceNum <= 0);

  return (
    <React.Fragment>
      <Head title="Clinic Prices"></Head>
      <Content>
        <Alert color="light" className="mb-3">
          <strong>Partner clinic prices</strong> — category roots for the grid are{" "}
          <strong>platform-wide</strong>. Admins edit them under{" "}
          <Button
            color="primary"
            size="sm"
            className="ms-1"
            type="button"
            onClick={() => history.push(getPublicUrl("platform-catalog-roots"))}
          >
            Platform catalog roots
          </Button>
        </Alert>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle tag="h3" page>
                Prices /{" "}
                <strong className="text-primary">
                  {clinic.name || clinic.clinicName || "N/A"}
                </strong>
              </BlockTitle>
              <BlockDes className="text-soft">
                <ul className="list-inline">
                  <li>
                    Partner:{" "}
                    <span className="text-base">
                      {partner?.organization_name || "N/A"}
                    </span>
                  </li>
                  <li>
                    Clinic currency (TM + TC):{" "}
                    <span className="text-base">{clinic?.defaultCurrency || globalCurrency || "—"}</span>
                  </li>
                  {autoSaving && (
                    <li>
                      <span className="badge bg-info">
                        <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                        Saving…
                      </span>
                    </li>
                  )}
                  {hasUnsavedChanges && !autoSaving && !saving && (
                    <li>
                      <span className="badge bg-warning">
                        <Icon name="alert-circle" className="me-1" />
                        Unsaved changes
                      </span>
                    </li>
                  )}
                </ul>
              </BlockDes>
            </BlockHeadContent>
            <BlockHeadContent>
              {!ENABLE_PRICE_AUTOSAVE && (
                <Alert color="info" className="py-2 px-3 mb-0 me-2">
                  Auto-save is off. Click <strong>Save</strong> to persist price changes.
                </Alert>
              )}
              <Button
                color="primary"
                className="me-2"
                onClick={handleManualSave}
                disabled={saving || autoSaving || !hasUnsavedChanges}
              >
                {saving ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                    Saving…
                  </>
                ) : (
                  <>
                    <Icon name="save" className="me-1" />
                    Save
                  </>
                )}
              </Button>
              <Button
                color="light"
                outline
                className="bg-white d-none d-sm-inline-flex"
                onClick={() => {
                  if (hasUnsavedChanges) {
                    swal({
                      title: "Unsaved changes",
                      text: "You have unsaved changes. What would you like to do?",
                      icon: "warning",
                      buttons: {
                        save: {
                          text: "Save and leave",
                          value: "save",
                          className: "btn-primary"
                        },
                        cancel: {
                          text: "Cancel",
                          value: "cancel",
                          className: "btn-secondary"
                        },
                        discard: {
                          text: "Leave without saving",
                          value: "discard",
                          className: "btn-danger"
                        }
                      },
                      dangerMode: true
                    }).then((value) => {
                      if (value === "save") {
                        performSave(pricesMap, true).then(() => {
                          setHasUnsavedChanges(false);
                          history.goBack();
                        });
                      } else if (value === "discard") {
                        setHasUnsavedChanges(false);
                        history.goBack();
                      }
                    });
                  } else {
                    history.goBack();
                  }
                }}
              >
                <Icon name="arrow-left"></Icon>
                <span>Back</span>
              </Button>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        <Block>
          <p className="text-soft small mb-3">
            <span className="text-soft">Offers teleconsultation:</span>{" "}
            {clinic?.offersTeleconsultation === true ? "Yes" : "No"} · Default TC price and session length apply at clinic level;
            the category grid below is separate. Per-practitioner overrides live on each practitioner. Enable or disable TC on the
            clinics list.
          </p>
          {showTcPositivePriceWarning ? (
            <Alert color="warning" className="mb-3" role="status">
              Teleconsultation is <strong>on</strong> for this clinic: set a <strong>default TC price greater than 0</strong> (in the
              clinic currency), then save (silent zero would confuse patients at checkout).
            </Alert>
          ) : null}

          <Card className="card-bordered mb-3 border-light">
            <div className="card-inner">
              <h6 className="overline-title mb-2">Price hierarchy</h6>
              <p className="text-soft small mb-0">
                When several amounts could apply, the platform resolves the effective price in this order:{" "}
                <strong>practitioner-specific price</strong> (if set) → <strong>product / category line price</strong> (grid below) →{" "}
                <strong>clinic default TC price</strong> (this page). Planning settings (duration, booking mode) do not override
                monetary amounts; they affect scheduling only.
              </p>
            </div>
          </Card>

          <h5 className="title mb-3">Planning</h5>
          <Card className="card-bordered mb-3">
            <div className="card-inner">
              <h6 className="overline-title mb-2">Booking mode</h6>
              <p className="text-soft small mb-3">
                How patients are matched to practitioners for this clinic. Platform approval and clinic active status are unrelated
                and are edited from the clinics list.
              </p>
              <Row className="g-3">
                <Col md="8" lg="6">
                  <label className="form-label small" htmlFor="tc-booking-mode">
                    TC booking mode
                  </label>
                  <select
                    id="tc-booking-mode"
                    className="form-control form-control-sm"
                    value={tcClinicForm.bookingMode}
                    onChange={(e) => setTcClinicForm((f) => ({ ...f, bookingMode: e.target.value }))}
                  >
                    <option value="clinic_pool">Clinic pool (opaque — clinic assigns practitioner)</option>
                    <option value="named_practitioner">Named practitioner (patient picks slot/doctor)</option>
                    <option value="solo">Solo practitioner structure</option>
                  </select>
                </Col>
              </Row>
            </div>
          </Card>
          <Card className="card-bordered mb-3">
            <div className="card-inner">
              <h6 className="overline-title mb-2">Session length (planning)</h6>
              <p className="text-soft small mb-3">
                Default teleconsultation duration in minutes (10–60). Used for scheduling and capacity; named-practitioner flows use
                this as the default slot length unless overridden per practitioner.
              </p>
              <Row className="g-3">
                <Col md="4" lg="3">
                  <label className="form-label small" htmlFor="tc-duration">
                    Duration (min)
                  </label>
                  <input
                    id="tc-duration"
                    type="number"
                    min={10}
                    max={60}
                    className="form-control form-control-sm"
                    value={tcClinicForm.duration}
                    onChange={(e) => setTcClinicForm((f) => ({ ...f, duration: e.target.value }))}
                  />
                </Col>
              </Row>
            </div>
          </Card>

          <h5 className="title mb-3">Pricing</h5>
          <Card className="card-bordered mb-3">
            <div className="card-inner">
              <h6 className="overline-title mb-2">Default TC price</h6>
              <p className="text-soft small mb-3">
                Amount is in the clinic&apos;s default currency (same as TM and the price grid). Change currency with{" "}
                <strong>Grid currency</strong> and <strong>Save default currency to clinic</strong> below. Category TC rows and
                Stripe should stay aligned with that currency.
              </p>
              <Row className="g-3 align-items-end">
                <Col md="4">
                  <label className="form-label small" htmlFor="tc-price">
                    TC price
                  </label>
                  <input
                    id="tc-price"
                    type="number"
                    min={0}
                    step={0.01}
                    className="form-control form-control-sm"
                    value={tcClinicForm.price}
                    onChange={(e) => setTcClinicForm((f) => ({ ...f, price: e.target.value }))}
                  />
                </Col>
                <Col md="auto">
                  <Button
                    color="primary"
                    size="sm"
                    disabled={savingTcClinic || !clinicId}
                    onClick={async () => {
                      setSavingTcClinic(true);
                      try {
                        const priceNum = parseFloat(String(tcClinicForm.price).replace(",", "."), 10);
                        const durNum = parseInt(tcClinicForm.duration, 10);
                        if (Number.isNaN(priceNum) || priceNum < 0) {
                          swal("Invalid", "Enter a valid TC price.", "warning");
                          return;
                        }
                        if (clinic?.offersTeleconsultation === true && priceNum <= 0) {
                          swal(
                            "Invalid",
                            "Teleconsultation is enabled for this clinic: enter a TC price greater than 0.",
                            "warning"
                          );
                          return;
                        }
                        if (Number.isNaN(durNum) || durNum < 10 || durNum > 60) {
                          swal("Invalid", "Duration must be between 10 and 60 minutes.", "warning");
                          return;
                        }
                        await http.put(
                          `/api/v1/partners/medical/clinics/${clinicId}`,
                          {
                            consultationPrice: priceNum,
                            consultationDurationMinutes: durNum,
                            teleconsultationBookingMode: tcClinicForm.bookingMode,
                          },
                          { suppressInlineErrorSwal: true }
                        );
                        const refreshed = await http.get(`/api/v1/partners/medical/clinics/${clinicId}`, {
                          suppressInlineErrorSwal: true,
                        });
                        const c = refreshed.data?.data || refreshed.data;
                        setClinic(c);
                        swal("Saved", "Teleconsultation clinic defaults updated.", "success");
                      } catch (err) {
                        swal(
                          "TC defaults could not be saved",
                          formatAdminApiError(err, "Save failed"),
                          "error"
                        );
                      } finally {
                        setSavingTcClinic(false);
                      }
                    }}
                  >
                    {savingTcClinic ? "Saving…" : "Save TC defaults"}
                  </Button>
                </Col>
              </Row>
              <Button color="light" size="sm" className="mt-3 me-2" onClick={navigateBackFromClinicPrices}>
                {partnerBackId ? "Back to partner" : "Open clinics list"}
              </Button>
              <Button color="light" outline size="sm" className="mt-3 me-2" onClick={() => history.push(getPublicUrl("clinics"))}>
                All clinics
              </Button>
            </div>
          </Card>
          <h5 className="title mb-2">Category &amp; product prices</h5>
          <p className="text-soft small mb-3">
            Single grid: <strong>TM</strong> rows come from the medical tourism catalog root (fallback uid <code>48</code>);{" "}
            <strong>TC</strong> rows from the TC specialties root (fallback uid <code>192</code>). Configure roots under{" "}
            <strong>Platform catalog roots</strong>. Use the channel filter to focus on TM or TC. Changing <strong>Grid currency</strong>{" "}
            updates draft amounts for all loaded rows; click <strong>Save now</strong> to persist prices. Use <strong>Save default currency</strong>{" "}
            to store the clinic&apos;s default currency (TM + TC) without saving the full price sheet.
          </p>
          <Card className="card-bordered">
            <div className="card-inner">
              <Row className="g-gs mb-4">
                <Col md="5">
                  <FormGroup>
                    <label className="form-label" htmlFor="clinic-price-search">
                      Search category
                    </label>
                    <input
                      id="clinic-price-search"
                      type="text"
                      className="form-control"
                      placeholder="Search…"
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                    />
                  </FormGroup>
                </Col>
                <Col md="3">
                  <FormGroup>
                    <label className="form-label">Channel</label>
                    <select
                      className="form-control"
                      value={channelFilter}
                      onChange={(e) => setChannelFilter(e.target.value)}
                      aria-label="Filter by TM or TC"
                    >
                      <option value="all">All channels</option>
                      <option value="tm">Medical tourism (TM)</option>
                      <option value="tc">Teleconsultation (TC)</option>
                    </select>
                  </FormGroup>
                </Col>
                <Col md="4">
                  <FormGroup>
                    <label className="form-label" htmlFor="clinic-grid-currency">
                      Grid currency
                    </label>
                    <RSelect
                      inputId="clinic-grid-currency"
                      options={gridCurrencySelectProps.options}
                      value={gridCurrencySelectProps.value}
                      onChange={(opt) => {
                        const next = opt?.value || "USD";
                        setGlobalCurrency(next);
                        setPricesMap((prev) => {
                          const updated = { ...prev };
                          Object.keys(updated).forEach((key) => {
                            updated[key] = { ...updated[key], currency: next };
                          });
                          return updated;
                        });
                        setHasUnsavedChanges(true);
                      }}
                    />
                    {suggestedCurrencyFromCountry ? (
                      <p className="small text-soft mt-1 mb-0">
                        Suggested from clinic country ({clinic?.address?.country}):{" "}
                        <button
                          type="button"
                          className="btn btn-link btn-sm p-0 align-baseline"
                          onClick={() => {
                            const next = suggestedCurrencyFromCountry;
                            setGlobalCurrency(next);
                            setPricesMap((prev) => {
                              const updated = { ...prev };
                              Object.keys(updated).forEach((key) => {
                                updated[key] = { ...updated[key], currency: next };
                              });
                              return updated;
                            });
                            setHasUnsavedChanges(true);
                          }}
                        >
                          {suggestedCurrencyFromCountry}
                        </button>{" "}
                        (optional)
                      </p>
                    ) : null}
                    <Button
                      color="light"
                      size="sm"
                      className="mt-2"
                      type="button"
                      disabled={savingClinicCurrency || !clinicId}
                      onClick={saveClinicDefaultCurrency}
                    >
                      {savingClinicCurrency ? "Saving…" : "Save default currency to clinic"}
                    </Button>
                  </FormGroup>
                </Col>
              </Row>

              <div className="table-responsive" style={{ position: "relative", zIndex: 1 }}>
                <table className="table table-hover" style={{ position: "relative", zIndex: 1 }}>
                  <thead>
                    <tr>
                      <th style={{ width: "32%" }}>Category</th>
                      <th style={{ width: "10%" }}>Channel</th>
                      <th style={{ width: "14%" }}>Price</th>
                      <th style={{ width: "9%" }}>Duration</th>
                      <th style={{ width: "23%" }}>Description</th>
                      <th style={{ width: "10%" }}>Active</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleCategories.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="text-center text-soft">
                          {mergedCategoriesFlat.length === 0
                            ? "Loading categories…"
                            : "No category found"}
                        </td>
                      </tr>
                    ) : (
                      visibleCategories.map((category, index) => {
                        const uniqueKey = category.path || `${category.id}-${index}`;
                        if (category.isHeader) {
                          return (
                            <tr
                              key={uniqueKey}
                              style={{
                                backgroundColor: "#f8f9fa",
                                cursor: "pointer",
                                position: "relative",
                                zIndex: 10,
                                pointerEvents: "auto",
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleHeader(category.id);
                              }}
                            >
                              <td colSpan={6}>
                                <Icon
                                  name={
                                    expandedHeaders.has(String(category.id))
                                      ? "chevron-down"
                                      : "chevron-right"
                                  }
                                  className="me-2"
                                />
                                <strong>{category.label}</strong>
                              </td>
                            </tr>
                          );
                        }

                        const catId = String(category.id);
                        const rowCurrency = defaultCurrencyForCategory(catId);
                        const priceData =
                          pricesMap[catId] ||
                          ({
                            price: 0,
                            currency: rowCurrency,
                            isActive: false,
                            description: "",
                            duration: 0,
                          });
                        const priceInputKey = `${catId}-price`;
                        const durationInputKey = `${catId}-duration`;
                        const ch = category.catalogChannel === "tc" ? "tc" : "tm";

                        return (
                          <tr key={uniqueKey}>
                            <td>
                              <span style={{ paddingLeft: `${category.level * 20}px` }}>{category.label}</span>
                            </td>
                            <td>
                              {ch === "tc" ? (
                                <span className="badge badge-dim bg-info">TC</span>
                              ) : (
                                <span className="badge badge-dim bg-secondary">TM</span>
                              )}
                            </td>
                            <td>
                              <div className="input-group">
                                <span className="input-group-text">
                                  {getClinicPriceCurrencySymbol(priceData.currency || rowCurrency)}
                                </span>
                                <input
                                  type="text"
                                  className="form-control"
                                  min="0"
                                  step="0.01"
                                  data-category-id={catId}
                                  data-field="price"
                                  value={
                                    inputValues[priceInputKey] !== undefined
                                      ? inputValues[priceInputKey]
                                      : priceData.price !== undefined && priceData.price !== null
                                        ? String(priceData.price)
                                        : ""
                                  }
                                  onChange={(e) => handlePriceChange(category.id, "price", e.target.value)}
                                  onBlur={() => handlePriceBlur(category.id, "price")}
                                  placeholder="0.00"
                                />
                              </div>
                            </td>
                            <td>
                              <input
                                type="text"
                                className="form-control"
                                min="0"
                                data-category-id={catId}
                                data-field="duration"
                                value={
                                  inputValues[durationInputKey] !== undefined
                                    ? inputValues[durationInputKey]
                                    : priceData.duration !== undefined && priceData.duration !== null
                                      ? String(priceData.duration)
                                      : ""
                                }
                                onChange={(e) => handlePriceChange(category.id, "duration", e.target.value)}
                                onBlur={() => handlePriceBlur(category.id, "duration")}
                                placeholder="0"
                              />
                            </td>
                            <td>
                              <input
                                type="text"
                                className="form-control"
                                value={priceData.description || ""}
                                onChange={(e) => handlePriceChange(category.id, "description", e.target.value)}
                                placeholder="Optional description"
                              />
                            </td>
                            <td className="text-center">
                              <div className="custom-control custom-switch">
                                <input
                                  type="checkbox"
                                  className="custom-control-input"
                                  id={`active-${catId}`}
                                  checked={priceData.isActive || false}
                                  onChange={(e) => handlePriceChange(category.id, "isActive", e.target.checked)}
                                />
                                <label className="custom-control-label" htmlFor={`active-${catId}`} />
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              <div className="text-center mt-4">
                <Button
                  color="primary"
                  size="lg"
                  onClick={handleManualSave}
                  disabled={saving || autoSaving || !hasUnsavedChanges}
                >
                  {saving ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                      Saving…
                    </>
                  ) : (
                    <>
                      <Icon name="save" className="me-2" />
                      Save now
                    </>
                  )}
                </Button>
              </div>

              <div className="alert alert-info mt-4 mb-0">
                <Icon name="info" className="me-2" />
                <strong>Note:</strong> Changes are <strong>not</strong> saved automatically. Click <strong>Save now</strong> to persist
                your changes. <strong>Save default currency to clinic</strong> updates only the clinic&apos;s default TM currency.
              </div>
            </div>
          </Card>
        </Block>
      </Content>
    </React.Fragment>
  );
};

export default ClinicPrices;

